import {
  cidrToRange,
  findFreeGaps,
  formatCidr,
  normalizeCidr,
  overlaps,
  parseCidr,
  prefixToBlockSize,
  prefixToMask,
  splitGap,
} from './cidrUtils';
import type { AddressRange, IpCountMode } from './types';
import { isAwsSubnetAllowed } from './usableIpUtils';

export type SplitBlockResult =
  | { ok: true }
  | { ok: false; reason: 'SIBLING_GAP' | 'MAX_PREFIX' };

/**
 * 既存ブロックをクリック分割できるか。
 * ブロック内に重なる別ブロックがある場合のみ不可（空き領域の分割とは別ルール）。
 */
export function canSplitBlock(blockCidr: string, childCidrs: string[]): SplitBlockResult {
  const block = parseCidr(blockCidr);
  if (block.prefix >= 32) {
    return { ok: false, reason: 'MAX_PREFIX' };
  }

  const blockRange = cidrToRange(blockCidr);
  const normalizedBlock = normalizeCidr(blockCidr);

  for (const cidr of childCidrs) {
    const normalized = normalizeCidr(cidr);
    if (normalized === normalizedBlock) {
      continue;
    }
    if (overlaps(cidrToRange(normalized), blockRange)) {
      return { ok: false, reason: 'SIBLING_GAP' };
    }
  }

  return { ok: true };
}

export type SplitOption = {
  prefix: number;
  cidr: string;
  blockSize: bigint;
};

export type NSplitOption = {
  n: number;
  prefix: number;
  cidrs: string[];
  blockSize: bigint;
};

function largestPowerOfTwoAtMost(value: bigint): bigint {
  if (value <= BigInt(0)) {
    return BigInt(0);
  }
  let power = BigInt(1);
  while ((power << BigInt(1)) <= value) {
    power <<= BigInt(1);
  }
  return power;
}

function prefixFromBlockSize(blockSize: bigint): number {
  let prefix = 32;
  let size = BigInt(1);
  while (size < blockSize) {
    size <<= BigInt(1);
    prefix -= 1;
  }
  return prefix;
}

/**
 * 空き区間を N 等分する CIDR 一覧を返す（CIDR 境界に揃えられる場合のみ）
 */
export function splitGapIntoN(gap: AddressRange, n: number): NSplitOption | null {
  if (n < 1 || !Number.isInteger(n)) {
    return null;
  }

  if (n === 1) {
    for (let prefix = 16; prefix <= 32; prefix += 1) {
      const cidr = splitGap(gap, prefix);
      if (cidr) {
        return {
          n: 1,
          prefix,
          cidrs: [cidr],
          blockSize: prefixToBlockSize(prefix),
        };
      }
    }
    return null;
  }

  const gapSize = gap.end - gap.start + BigInt(1);
  const nBig = BigInt(n);
  let blockSize = largestPowerOfTwoAtMost(gapSize / nBig);

  while (blockSize >= BigInt(1)) {
    const prefix = prefixFromBlockSize(blockSize);
    const mask = prefixToMask(prefix);
    let network = gap.start & mask;
    if (network < gap.start) {
      network += blockSize;
    }

    const cidrs: string[] = [];
    let cursor = network;
    for (let i = 0; i < n; i += 1) {
      const blockEnd = cursor + blockSize - BigInt(1);
      if (blockEnd > gap.end) {
        break;
      }
      cidrs.push(formatCidr(cursor, prefix));
      cursor += blockSize;
    }

    if (cidrs.length === n) {
      return { n, prefix, cidrs, blockSize };
    }

    blockSize >>= BigInt(1);
  }

  return null;
}

/**
 * 空き区間で可能な N 分割候補を返す
 */
export function getNSplitOptionsForGap(gap: AddressRange, maxN = 32): NSplitOption[] {
  const options: NSplitOption[] = [];

  for (let n = 1; n <= maxN; n += 1) {
    const result = splitGapIntoN(gap, n);
    if (result) {
      options.push(result);
    }
  }

  return options;
}

/**
 * 範囲を N 等分できる候補（2 分割以上）を返す
 */
export function getNSplitOptionsForRange(range: AddressRange, maxN = 32): NSplitOption[] {
  return getNSplitOptionsForGap(range, maxN).filter((option) => option.n >= 2);
}

/**
 * 既存ブロックを N 等分できる候補を返す
 */
export function getNSplitOptionsForBlock(cidr: string, maxN = 32): NSplitOption[] {
  return getNSplitOptionsForRange(cidrToRange(cidr), maxN);
}

/**
 * 空き区間に収まるプレフィックス候補を大きい順で返す
 */
export function getSplitOptionsForGap(gap: AddressRange, minPrefix = 16, maxPrefix = 32): SplitOption[] {
  const gapSize = gap.end - gap.start + BigInt(1);
  const options: SplitOption[] = [];

  for (let prefix = minPrefix; prefix <= maxPrefix; prefix += 1) {
    const blockSize = prefixToBlockSize(prefix);
    if (blockSize > gapSize) {
      continue;
    }
    const cidr = splitGap(gap, prefix);
    if (cidr) {
      options.push({ prefix, cidr, blockSize });
    }
  }

  return options.sort((a, b) => a.prefix - b.prefix);
}

/**
 * 親 CIDR と既存ブロックから空き区間一覧を返す
 */
export function findGaps(parentCidr: string, childCidrs: string[]): AddressRange[] {
  return findFreeGaps(parentCidr, childCidrs);
}

/**
 * 空き区間内で最大サイズの分割 CIDR を返す
 */
export function getLargestSplitForGap(gap: AddressRange): SplitOption | null {
  const options = getSplitOptionsForGap(gap);
  if (options.length === 0) {
    return null;
  }
  return options[0];
}

/**
 * 空き区間の先頭に収まる最大 CIDR を返す（AWS モードでは /28 以上）
 */
export function getLargestCidrForGap(gap: AddressRange, ipCountMode: IpCountMode = 'standard'): string | null {
  const options = getSplitOptionsForGap(gap);
  for (const option of options) {
    if (isAwsSubnetAllowed(option.prefix, ipCountMode)) {
      return option.cidr;
    }
  }
  return null;
}
